// src/pages/customers/CustomersPage.tsx
import {
  Calendar,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  Download,
  Loader2,
  Plus,
  RefreshCw,
  Router,
  Search,
  Smartphone,
  TrendingUp,
  User,
  Users,
  XCircle,
} from "lucide-react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "../../components/ui/Toast";
import { API_CONFIG, fetchApp } from "../../services/apiService";
import { CustomerSummary, PlanLite } from "../../types/customers";

type StatusFilter = "all" | "active" | "inactive";

interface CustomerRow extends CustomerSummary {
  customer_id?: string | null;
  customer_name?: string | null;
  plan?: PlanLite | null;
}

interface CustomerSummaryApiResponse {
  items: CustomerSummary[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  stats?: {
    total?: number;
    active?: number;
    new_this_month?: number;
  };
  routers?: Array<{
    id: string;
    name: string;
  }>;
}

const customerSummaryCache = new Map<
  string,
  CustomerSummaryApiResponse | CustomerSummary[]
>();

function useDebouncedValue<T>(value: T, delay = 450): T {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

const formatKES = (value: number) =>
  `KES ${new Intl.NumberFormat("en-KE").format(value)}`;

const formatDate = (value?: string | null) => {
  if (!value) return "—";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) {
    return value;
  }

  return d.toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getInitials = (name: string) =>
  String(name || "")
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "C";

const AVATAR_PALETTES = [
  "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200",
  "bg-sky-100 text-sky-700 ring-1 ring-sky-200",
  "bg-violet-100 text-violet-700 ring-1 ring-violet-200",
  "bg-amber-100 text-amber-700 ring-1 ring-amber-200",
  "bg-teal-100 text-teal-700 ring-1 ring-teal-200",
  "bg-rose-100 text-rose-700 ring-1 ring-rose-200",
];

const avatarColor = (name: string) =>
  AVATAR_PALETTES[
    (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) %
      AVATAR_PALETTES.length
  ];

const getCustomerDisplayName = (customer: CustomerRow) =>
  customer.customer_name || customer.username || "Unknown Customer";

const getPositiveNumber = (value: string | null, fallback: number) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const CustomersPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const [exporting, setExporting] = useState(false);

  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [search, setSearch] = useState(() => searchParams.get("q") || "");
  const debouncedSearch = useDebouncedValue(search, 450);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => {
    const status = searchParams.get("status");
    return status === "active" || status === "inactive" ? status : "all";
  });
  const [routerFilter, setRouterFilter] = useState<string>(
    () => searchParams.get("router_id") || "all",
  );

  const [page, setPage] = useState(() =>
    getPositiveNumber(searchParams.get("page"), 1),
  );
  const [pageSize, setPageSize] = useState(() =>
    getPositiveNumber(searchParams.get("page_size"), 20),
  );
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    newThisMonth: 0,
  });

  const [routers, setRouters] = useState<Array<[string, string]>>([]);

  const cacheRef = useRef(customerSummaryCache);
  const requestIdRef = useRef(0);

  const buildListSearchParams = () => {
    const params = new URLSearchParams();

    params.set("page", String(page));
    params.set("page_size", String(pageSize));

    if (debouncedSearch.trim()) {
      params.set("q", debouncedSearch.trim());
    }

    if (statusFilter !== "all") {
      params.set("status", statusFilter);
    }

    if (routerFilter !== "all") {
      params.set("router_id", routerFilter);
    }

    return params;
  };

  const buildCustomerSummaryUrl = () => {
    const params = buildListSearchParams();
    return `${API_CONFIG.baseUrl}/customers/summary?${params.toString()}`;
  };

  const openCustomerDetails = (secretId: string | number) => {
    const params = buildListSearchParams();
    const returnTo = `${location.pathname}?${params.toString()}`;

    navigate(`/customers/${secretId}`, {
      state: { from: returnTo },
    });
  };

  const normalizeCustomers = (items: CustomerSummary[]): CustomerRow[] => {
    return items.map((c) => ({
      ...c,
      plan: c.plan_name
        ? {
            id: c.plan_id ?? undefined,
            name: c.plan_name,
            price: (c.plan_price_cents ?? 0) / 100,
            speed: null,
          }
        : null,
    }));
  };

  const deriveFallbackStats = (items: CustomerSummary[], apiTotal: number) => {
    const activeCount = items.filter((c) => c.status).length;
    const now = new Date();
    const lastMonth = new Date(
      now.getFullYear(),
      now.getMonth() - 1,
      now.getDate(),
    );

    return {
      total: apiTotal,
      active: activeCount,
      newThisMonth: items.filter((c) => new Date(c.created_at) >= lastMonth)
        .length,
    };
  };

  const deriveFallbackRouters = (items: CustomerSummary[]) => {
    const map = new Map<string, string>();

    items.forEach((c) => {
      const id = String(c.router_id || "");
      if (!id) return;

      if (!map.has(id)) {
        map.set(id, c.router_name || `Router #${id}`);
      }
    });

    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  };

  const applySummaryResponse = (
    response: CustomerSummaryApiResponse | CustomerSummary[],
  ) => {
    if (Array.isArray(response)) {
      const items = response;

      setCustomers(normalizeCustomers(items));
      setTotal(items.length);
      setTotalPages(Math.max(1, Math.ceil(items.length / pageSize)));
      setStats(deriveFallbackStats(items, items.length));
      setRouters(deriveFallbackRouters(items));

      return;
    }

    const items = response.items || [];
    const apiTotal = Number(response.total ?? items.length);
    const apiTotalPages = Number(
      response.total_pages ?? Math.max(1, Math.ceil(apiTotal / pageSize)),
    );

    setCustomers(normalizeCustomers(items));
    setTotal(apiTotal);
    setTotalPages(Math.max(1, apiTotalPages));

    if (response.stats) {
      setStats({
        total: Number(response.stats.total ?? 0),
        active: Number(response.stats.active ?? 0),
        newThisMonth: Number(response.stats.new_this_month ?? 0),
      });
    } else {
      setStats(deriveFallbackStats(items, apiTotal));
    }

    if (Array.isArray(response.routers)) {
      setRouters(
        response.routers.map((r) => [
          String(r.id),
          r.name || `Router #${r.id}`,
        ]),
      );
    } else {
      setRouters(deriveFallbackRouters(items));
    }
  };

  const loadCustomers = async (force = false) => {
    const url = buildCustomerSummaryUrl();

    if (!force && cacheRef.current.has(url)) {
      applySummaryResponse(cacheRef.current.get(url)!);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);

    try {
      const res = await fetchApp(url);

      if (requestId !== requestIdRef.current) return;

      cacheRef.current.set(url, res);
      applySummaryResponse(res);
    } catch (e: any) {
      if (requestId !== requestIdRef.current) return;

      showToast(e?.message || "Failed to load customers.", "error");
      setCustomers([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSyncAll = async () => {
    try {
      setSyncing(true);

      const res = await fetchApp(`${API_CONFIG.baseUrl}/secrets/sync`, {
        method: "POST",
      });

      if (res && typeof res === "object" && "routers" in (res as any)) {
        const data = res as any;
        const failed = Number(data.failed_routers ?? 0);
        const totalSynced = Number(data.total_synced ?? 0);

        if (failed > 0) {
          const failedList = (data.routers || [])
            .filter((r: any) => r?.ok === false)
            .map(
              (r: any) =>
                `${r.router_name || `Router #${r.router_id}`}: ${
                  r.error || "Failed"
                }`,
            )
            .slice(0, 3);

          showToast(
            `Sync completed with errors. Synced ${totalSynced}. ${failedList.join(
              " | ",
            )}`,
            "error",
          );
        } else {
          showToast(
            `Sync successful. Synced ${totalSynced} customers.`,
            "success",
          );
        }
      } else {
        showToast("Customers synced successfully!", "success");
      }

      cacheRef.current.clear();
      setPage(1);
      await loadCustomers(true);
    } catch (err: any) {
      showToast(
        err?.message || "Failed to sync customers from MikroTik.",
        "error",
      );
    } finally {
      setSyncing(false);
    }
  };

  const getAuthToken = () => {
    return (
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken")
    );
  };

  const buildSecretsExportUrl = () => {
    const params = new URLSearchParams();
    params.set("status", statusFilter);
    if (routerFilter !== "all") {
      params.set("router_id", routerFilter);
    }

    return `${API_CONFIG.baseUrl}/customers/secrets-report/pdf?${params.toString()}`;
  };

  const handleExportSecretsPdf = async () => {
    try {
      setExporting(true);

      const url = buildSecretsExportUrl();
      const token = getAuthToken();

      const res = await fetch(url, {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok) {
        let errorMessage = "Failed to export customers report.";

        try {
          const errorData = await res.json();
          errorMessage =
            errorData?.detail || errorData?.message || errorMessage;
        } catch {
          // ignore non-json errors
        }

        throw new Error(errorMessage);
      }

      const blob = await res.blob();

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const routerLabel =
        routerFilter === "all"
          ? "all-routers"
          : routers.find(([id]) => id === routerFilter)?.[1] ||
            "selected-router";

      const safeRouterLabel = routerLabel
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

      const fileName = `pppoe-customers-${statusFilter}-${safeRouterLabel}.pdf`;

      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(downloadUrl);

      showToast("PDF report exported successfully.", "success");
    } catch (err: any) {
      showToast(err?.message || "Failed to export PDF report.", "error");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    setSearchParams(buildListSearchParams(), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch, statusFilter, routerFilter]);

  useLayoutEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch, statusFilter, routerFilter]);

  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="min-h-screen bg-[#eef3f8] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Customer Management
            </div>

            <h1 className="mt-3 text-[24px] font-bold tracking-[-0.5px] text-slate-900">
              PPPoE Customers
            </h1>

            <p className="mt-1 text-sm text-slate-500">
              Manage subscribers, plans and connectivity status.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <button
              type="button"
              onClick={() => navigate("/customers/add")}
              className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/20 transition-all hover:from-emerald-600 hover:to-teal-600 active:scale-[0.98]"
            >
              <Plus className="h-4 w-4" />
              <span>Add Customer</span>
            </button>

            <button
              type="button"
              onClick={handleSyncAll}
              disabled={loading || syncing}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-600 shadow-sm transition-all hover:bg-slate-100 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]"
            >
              {syncing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span>{syncing ? "Syncing…" : "Sync Secrets"}</span>
            </button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 px-5 py-5 shadow-sm shadow-slate-300/40">
            <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-slate-200/60" />

            <div className="relative flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-200 text-slate-700">
                <Users className="h-5 w-5" />
              </div>

              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
                  Total
                </p>
                <p className="mt-0.5 text-3xl font-black tracking-tight text-slate-900">
                  {stats.total}
                </p>
                <p className="text-[11px] font-medium text-slate-500">
                  Registered accounts
                </p>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 px-5 py-5 shadow-sm shadow-emerald-200/50">
            <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-emerald-200/50" />

            <div className="relative flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                <TrendingUp className="h-5 w-5" />
              </div>

              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-widest text-emerald-600">
                  Active
                </p>
                <p className="mt-0.5 text-3xl font-black tracking-tight text-emerald-800">
                  {stats.active}
                </p>
                <p className="text-[11px] font-medium text-emerald-600">
                  Currently enabled
                </p>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-3xl border border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 px-5 py-5 shadow-sm shadow-sky-200/50">
            <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-sky-200/50" />

            <div className="relative flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-sky-100 text-sky-700">
                <Calendar className="h-5 w-5" />
              </div>

              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-widest text-sky-600">
                  New This Month
                </p>
                <p className="mt-0.5 text-3xl font-black tracking-tight text-sky-800">
                  {stats.newThisMonth}
                </p>
                <p className="text-[11px] font-medium text-sky-600">
                  Last 30 days
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main card */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          {/* Toolbar */}
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/70 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:w-[440px]">
              <Search className="absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search name, account, email, phone, router…"
                className="h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 pl-10 pr-3 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as StatusFilter);
                  setPage(1);
                }}
                className="h-10 cursor-pointer rounded-2xl border border-slate-300 bg-slate-50 px-3 text-[13px] font-semibold text-slate-600 transition-colors hover:bg-slate-100 focus:border-emerald-400 focus:outline-none"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              <select
                value={routerFilter}
                onChange={(e) => {
                  setRouterFilter(e.target.value);
                  setPage(1);
                }}
                className="h-10 cursor-pointer rounded-2xl border border-slate-300 bg-slate-50 px-3 text-[13px] font-semibold text-slate-600 transition-colors hover:bg-slate-100 focus:border-emerald-400 focus:outline-none"
              >
                <option value="all">All Routers</option>
                {routers.map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={handleExportSecretsPdf}
                disabled={exporting}
                className="inline-flex h-10 items-center gap-1.5 rounded-2xl border border-slate-300 bg-slate-50 px-3 text-[13px] font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {exporting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}

                <span className="hidden sm:inline">
                  {exporting ? "Exporting…" : "Export"}
                </span>
              </button>
            </div>
          </div>

          {/* Desktop table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100">
                  {[
                    "Customer",
                    "Contact",
                    "Plan",
                    "Status",
                    "Expiry",
                    "Joined",
                    "Account #",
                    "",
                  ].map((h, i) => (
                    <th
                      key={i}
                      className={`px-5 py-3.5 text-[11px] font-black uppercase tracking-widest text-slate-500 ${
                        i === 6 ? "text-right" : ""
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      {Array.from({ length: 8 }).map((_, j) => (
                        <td key={j} className="px-5 py-4">
                          <div
                            className="h-4 rounded-lg bg-slate-200"
                            style={{ width: `${60 + ((j * 13) % 40)}%` }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : customers.length > 0 ? (
                  customers.map((c) => {
                    const displayName = getCustomerDisplayName(c);
                    const initials = getInitials(displayName);
                    const palette = avatarColor(displayName);

                    const isExpiringSoon = (() => {
                      if (!c.service_expires_at) return false;

                      const exp = new Date(c.service_expires_at);
                      const diff =
                        (exp.getTime() - Date.now()) / (1000 * 60 * 60 * 24);

                      return diff >= 0 && diff <= 7;
                    })();

                    return (
                      <tr
                        key={String(c.secret_id)}
                        onClick={() => openCustomerDetails(c.secret_id)}
                        className="group cursor-pointer transition-colors hover:bg-emerald-50/60"
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div
                              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[12px] font-black ${palette}`}
                            >
                              {initials}
                            </div>

                            <div className="min-w-0">
                              <p className="truncate text-[13px] font-bold text-slate-900">
                                {displayName}
                              </p>

                              <p className="mt-0.5 flex items-center gap-1 text-[11px] font-medium text-slate-500">
                                <Router className="h-3 w-3 shrink-0 text-slate-400" />
                                <span className="truncate">
                                  {c.router_name || `Router #${c.router_id}`}
                                </span>
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <p className="flex items-center gap-1.5 text-[13px] font-medium text-slate-700">
                            <User className="h-3 w-3 shrink-0 text-slate-400" />
                            {c.email || "—"}
                          </p>

                          <p className="mt-0.5 flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
                            <Smartphone className="h-3 w-3 shrink-0" />
                            {c.phone_number || "—"}
                          </p>
                        </td>

                        <td className="px-5 py-4">
                          <p className="text-[13px] font-bold text-slate-800">
                            {c.plan?.name || c.plan_name || "No Plan"}
                          </p>

                          <p className="mt-0.5 flex items-center gap-1 text-[11px] font-medium text-slate-500">
                            <CreditCard className="h-3 w-3 shrink-0" />
                            {c.amount_payable != null
                              ? formatKES(c.amount_payable)
                              : "—"}
                          </p>
                        </td>

                        <td className="px-5 py-4">
                          {c.status ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-800">
                              <CheckCircle2 className="h-3 w-3" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-bold text-slate-600">
                              <XCircle className="h-3 w-3" />
                              Inactive
                            </span>
                          )}
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`text-[13px] ${
                              isExpiringSoon
                                ? "font-bold text-amber-700"
                                : "font-medium text-slate-600"
                            }`}
                          >
                            {formatDate(c.service_expires_at)}
                          </span>

                          {isExpiringSoon && (
                            <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-600">
                              Expiring soon
                            </p>
                          )}
                        </td>

                        <td className="px-5 py-4 text-[13px] font-medium text-slate-500">
                          {formatDate(c.created_at)}
                        </td>

                        <td className="px-5 py-4 text-right">
                          <span className="rounded-xl bg-slate-200 px-2.5 py-1 font-mono text-[11px] font-semibold text-slate-600">
                            {c.account_number || "—"}
                          </span>
                        </td>

                        <td className="px-3 py-4">
                          <ChevronRight className="h-4 w-4 text-slate-300 transition-colors group-hover:text-emerald-500" />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="py-20 text-center">
                      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-200">
                        <Users className="h-6 w-6 text-slate-500" />
                      </div>

                      <p className="text-[14px] font-bold text-slate-800">
                        No customers found
                      </p>

                      <p className="mx-auto mt-1 max-w-xs text-[13px] text-slate-500">
                        {search ||
                        statusFilter !== "all" ||
                        routerFilter !== "all"
                          ? "No results matched your search or filters."
                          : "Sync secrets or add your first customer to get started."}
                      </p>

                      {search ||
                      statusFilter !== "all" ||
                      routerFilter !== "all" ? (
                        <button
                          type="button"
                          onClick={() => {
                            setSearch("");
                            setStatusFilter("all");
                            setRouterFilter("all");
                            setPage(1);
                          }}
                          className="mt-4 text-[13px] font-bold text-emerald-700 hover:text-emerald-800"
                        >
                          Clear filters
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => navigate("/customers/add")}
                          className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-2 text-sm font-semibold text-white hover:from-emerald-600 hover:to-teal-600"
                        >
                          <Plus className="h-4 w-4" />
                          Add Customer
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="flex animate-pulse items-center gap-3 px-4 py-4"
                >
                  <div className="h-10 w-10 shrink-0 rounded-full bg-slate-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 w-40 rounded bg-slate-200" />
                    <div className="h-3 w-28 rounded bg-slate-200" />
                  </div>
                  <div className="h-6 w-14 rounded-full bg-slate-200" />
                </div>
              ))
            ) : customers.length === 0 ? (
              <div className="px-4 py-16 text-center">
                <Users className="mx-auto mb-3 h-10 w-10 text-slate-400" />

                <p className="text-[14px] font-bold text-slate-800">
                  No customers found
                </p>

                <p className="mt-1 text-[13px] text-slate-500">
                  {search || statusFilter !== "all" || routerFilter !== "all"
                    ? "No results matched your search or filters."
                    : "Add your first customer to get started."}
                </p>

                {(search ||
                  statusFilter !== "all" ||
                  routerFilter !== "all") && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearch("");
                      setStatusFilter("all");
                      setRouterFilter("all");
                      setPage(1);
                    }}
                    className="mt-3 text-[13px] font-bold text-emerald-700"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              customers.map((c) => {
                const displayName = getCustomerDisplayName(c);
                const initials = getInitials(displayName);
                const palette = avatarColor(displayName);

                return (
                  <button
                    key={String(c.secret_id)}
                    type="button"
                    onClick={() => openCustomerDetails(c.secret_id)}
                    className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-emerald-50 active:bg-emerald-100"
                  >
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[12px] font-black ${palette}`}
                    >
                      {initials}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13.5px] font-bold text-slate-900">
                        {displayName}
                      </p>

                      <p className="mt-0.5 truncate text-[11.5px] font-medium text-slate-500">
                        {c.plan?.name || c.plan_name || "No Plan"}

                        {c.amount_payable != null && (
                          <>
                            <span className="ml-1.5 text-slate-300">·</span>
                            <span className="ml-1.5">
                              {formatKES(c.amount_payable)}
                            </span>
                          </>
                        )}
                      </p>

                      <p className="mt-0.5 text-[11px] font-medium text-slate-400">
                        {c.router_name || `Router #${c.router_id}`}
                      </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      {c.status ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800">
                          <CheckCircle2 className="h-2.5 w-2.5" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-200 px-2 py-0.5 text-[10px] font-bold text-slate-600">
                          <XCircle className="h-2.5 w-2.5" />
                          Inactive
                        </span>
                      )}

                      <ChevronRight className="h-4 w-4 text-slate-300" />
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Pagination */}
          <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-100/80 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[12.5px] font-medium text-slate-500">
              Page <span className="font-bold text-slate-800">{page}</span> of{" "}
              <span className="font-bold text-slate-800">{totalPages}</span>
              <span className="mx-2 text-slate-300">·</span>
              <span className="font-bold text-slate-700">{total}</span> results
            </p>

            <div className="flex flex-wrap gap-2">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                disabled={loading}
                className="h-9 rounded-2xl border border-slate-300 bg-slate-50 px-2 text-[12.5px] font-semibold text-slate-600 focus:border-emerald-400 focus:outline-none disabled:opacity-40"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>

              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={!canPrev || loading}
                className="h-9 rounded-2xl border border-slate-300 bg-slate-50 px-4 text-[12.5px] font-bold text-slate-600 shadow-sm transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
              >
                ← Prev
              </button>

              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={!canNext || loading}
                className="h-9 rounded-2xl border border-slate-300 bg-slate-50 px-4 text-[12.5px] font-bold text-slate-600 shadow-sm transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomersPage;
